import {Component, Input, OnDestroy} from '@angular/core';

import {INotification, INotificationDuplicateBarcodeError, NotificationResponseTypeEnum} from '../../models';

import {DEFAULT_LOADER_INFINITE} from '../../../../shared/shared-module-ui/models/animations/loading-g.interface';

import {setGlobalModalVisibility} from '../../../user-access/user-store/user.actions';

import {ModalType} from '../../../global-modal-container/models/modal-enums';

import {Store} from '@ngrx/store';

import {

  getStockUpdateChanges,

  setRetryStockUpdate,

  setSelectedNotification,
} from '../../store/notification.actions';

import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';

import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';

import {setNavigation, setSelectedStore} from '../../../user-access/user-stores-store/user-store.actions';
import {selectStores} from '../../../user-access/user-stores-store/user-store.selectors';

import {NavigationPageEnum} from '../../../user-access/models/navigations.enums';

import {AddStockItemComponent} from '../../../stock-manager/components/add-stock-item/add-stock-item.component';

import {ModalControllerService} from '../../../../shared/shared-services/overlay/modal-controller.service';

import {firstValueFrom, Subject} from 'rxjs';

import {filter, take, takeUntil, timeout} from 'rxjs/operators';

import {
  getStoreDepartmentsForStockManager,
  getLineColours,
  getStoreSuppliers,
} from '../../../stock-manager/store/stock-manager.actions';
import {getVatRateConversion} from '../../../../shared-modules/shared-grid/store/shared-grid.actions';
import {selectSelectedStockViewChanges} from '../../store/notification.selectors';
import {IStore} from '../../../../shared/shared-models/store/store';

import {parseDuplicateBarcodeErrorsFromXml} from '../../utils/notification-stock-update-error.utils';
import {SharedModalUtils} from '../../../../shared/shared-utils/shared-modal/shared-modal.utils';
import {Ascii} from '../../../../shared/shared-models/global/ascii.enum';



@Component({

  selector: 'app-notification-type-stock-update',

  templateUrl: './notification-type-stock-update.component.html',

  styleUrls: ['./notification-type-stock-update.component.scss'],

  standalone: false,

})

export class NotificationTypeStockUpdateComponent implements OnDestroy {



  isStockUpdateLoading = true;

  isLoadingRetryData = false;

  protected readonly iconsEnum = IconsEnum;

  protected readonly iconColourEnum = IconColourEnum;

  protected readonly notificationResponseTypeEnum = NotificationResponseTypeEnum;

  protected readonly duplicateBarcodeErrorMessage =

    'Barcodes already exist for items. Please change barcodes for existing items before retrying or retry without changing barcodes on these items.';



  protected readonly animation = DEFAULT_LOADER_INFINITE;



  private notification: INotification;

  private readonly destroy$ = new Subject<void>();



  constructor(

    private readonly store: Store,

    private modalController: ModalControllerService,

    private readonly sharedModalUtils: SharedModalUtils,

  ) {

  }



  get getIsComponentLoading(): boolean {

    return this.isStockUpdateLoading === false && !!this.getNotification;

  }



  get getNotification(): INotification {

    return this.notification;

  }



  get getNotificationOrderId(): string {

    return this.notification?.event;

  }



  get isDuplicateBarcodeError(): boolean {

    return this.getNotification?.responseType === NotificationResponseTypeEnum.duplicateBarcode;

  }



  get duplicateBarcodeErrors(): INotificationDuplicateBarcodeError[] {

    return parseDuplicateBarcodeErrorsFromXml(this.getNotification?.response ?? '');

  }



  @Input() set setIsStockUpdateLoading(isLoading: Record<string, boolean>) {

    if (this.getNotificationOrderId) {

      this.isStockUpdateLoading = isLoading?.[this.getNotificationOrderId] ?? true;

    }

  }



  @Input() set setNotification(notify: INotification) {

    this.notification = notify;

  }



  ngOnDestroy(): void {

    this.destroy$.next();

    this.destroy$.complete();

  }



  onViewChangesClick(): void {

    this.openRetryViewChanges(false, false);

  }

  /**
   * Open a modal with the full stock update error details.
   */
  protected async onViewErrorClick(): Promise<void> {
    const description: string[] = this.buildErrorDescription();
    if (description.length === 0) {
      return;
    }
    await this.sharedModalUtils.createModal({
      modalTitle: 'Stock Update Error',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.red,
      contextDescription: description,
      buttonAccept: false,
      buttonAcceptText: '',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Close',
      cssClass: ['shared-basic-modal-css-medium'],
    });
  }

  private buildErrorDescription(): string[] {
    const notification = this.getNotification;
    if (!notification?.response) {
      return ['An error occurred while processing this stock update.'];
    }
    const responseType = notification.responseType;
    if (responseType === NotificationResponseTypeEnum.newItemExists) {
      return ['This Stock Code Already Exists'];
    }
    if (responseType === NotificationResponseTypeEnum.duplicateBarcode) {
      const lines: string[] = [this.duplicateBarcodeErrorMessage, Ascii.EmptySpace];
      this.duplicateBarcodeErrors.forEach((item: INotificationDuplicateBarcodeError) => {
        lines.push(`${item.stockCode}: ${item.failedBarcodes.join(', ')}`);
      });
      return lines;
    }
    if (responseType === NotificationResponseTypeEnum.unknownError) {
      return ['An Unknown Error Occurred, please try again'];
    }
    return [notification.response];
  }



  onRetryFullStockUpdateClick(): void {

    this.openRetryViewChanges(true, false);

  }



  onRetryWithoutFailedBarcodesClick(): void {

    this.openRetryViewChanges(true, true);

  }



  private openRetryViewChanges(isRetry: boolean, excludeFailedBarcodes: boolean): void {

    if (isRetry && this.getNotification.retried) {
      return;
    }

    this.store.dispatch(setSelectedNotification({notification: this.notification}));

    this.store.dispatch(setRetryStockUpdate({retryStockUpdate: isRetry, excludeFailedBarcodes}));

    this.store.dispatch(getStockUpdateChanges({

      eventId: this.notification.event,

      storeId: this.notification.storeId,

    }));

    this.store.dispatch(setGlobalModalVisibility({

      visibility: true,

      modalType: [ModalType.Notifications, ModalType.ViewChanges],

    }));

  }



  async onRetryStockUpdateClick(): Promise<void> {

    if (this.getNotification.retried) {
      return;
    }

    if (this.isDuplicateBarcodeError) {

      this.onRetryFullStockUpdateClick();

      return;

    }



    if (this.notification.newStockItem) {
      this.isLoadingRetryData = true;

      const stores = await firstValueFrom(this.store.select(selectStores).pipe(take(1)));
      const matchedStore = stores.find((store: IStore) => store.storeId === this.notification.storeId);
      const selectedStore: IStore = matchedStore ?? {
        storeId: this.notification.storeId,
        name: this.notification.storeId,
      };

      this.store.dispatch(setSelectedNotification({notification: this.notification}));
      this.store.dispatch(setNavigation({
        feature: NavigationPageEnum.StockManager,
        navigation: {
          currentPage: NavigationPageEnum.StockManager,
          currentTab: null,
        },
      }));
      this.store.dispatch(setGlobalModalVisibility({
        visibility: false,
        modalType: [],
      }));
      this.store.dispatch(setSelectedStore({selectedStore}));
      this.dispatchRetryStockItemPreload(selectedStore);
      this.store.dispatch(getStockUpdateChanges({
        eventId: this.notification.event,
        storeId: this.notification.storeId,
      }));

      try {
        await firstValueFrom(
          this.store.select(selectSelectedStockViewChanges).pipe(
            filter((changes) => !!changes && Object.keys(changes).length > 0),
            take(1),
            timeout(15000),
            takeUntil(this.destroy$),
          ),
        );
      } catch (error) {
        console.warn('Retry stock item data preload incomplete:', error);
      } finally {
        this.isLoadingRetryData = false;
        await this.openAddStockItemRetryModal();
      }

      return;
    }



    this.openRetryViewChanges(true, false);

  }

  private dispatchRetryStockItemPreload(store: IStore): void {
    this.store.dispatch(getStoreDepartmentsForStockManager());
    this.store.dispatch(getLineColours({pageStoreDocument: 'line_colours'}));
    this.store.dispatch(getVatRateConversion());
    this.store.dispatch(getStoreSuppliers({store}));
  }

  private async openAddStockItemRetryModal(): Promise<void> {
    const modalController = await this.modalController.create({
      component: AddStockItemComponent,
      componentProps: {
        retryAddStockItem: true,
        reason: this.notification.responseType,
        retryNotification: this.notification,
      },
      cssClass: ['add-stock-item-modal'],
      backdropDismiss: false,
    });
    await modalController.present();
  }

}

